import React, {useEffect, useState} from 'react';
import {Alert, Button, Form, Input, Modal, Select, Space, Tag, Tooltip} from 'antd';
import {CloseOutlined, EditOutlined, PlusOutlined, SaveOutlined} from '@ant-design/icons';
import {createProjectTrm, updateProjectTrm} from '../../api/projectTrmApi';
import {getProjectDomainList} from '../../api/projectDomainApi';
import {getProjectWordList} from '../../api/projectWordApi';

function ProjectTrmCreateModal({ isOpen, onClose, onSuccess, pjtId, mode = 'create', initialValues = null }) {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [domainOptions, setDomainOptions] = useState([]);
  const [domainLoading, setDomainLoading] = useState(false);
  // 프로젝트 단어 목록 & 매칭 결과
  const [projectWords, setProjectWords] = useState([]);
  const [matchedWords, setMatchedWords] = useState([]);
  // 선택된 도메인 상세 정보(라벨 표시용)
  const [selectedDomainInfo, setSelectedDomainInfo] = useState(null);

  useEffect(() => {
    if (!isOpen) return;
    setError('');
    setDomainLoading(true);
    getProjectDomainList(pjtId)
      .then((data) => {
        const list = Array.isArray(data?.data) ? data.data : [];
        const opts = list.map((d) => {
          const domainType = d.domainType || d.type || d.dataType || d.domainTypeNm || '';
          return {
            value: d.pjtDomainId,
            label: `${d.domainNm}${d.domainEngNm ? ` (${d.domainEngNm})` : ''}${domainType ? ` [${domainType}]` : ''}`,
            domainNm: d.domainNm,
            domainEngNm: d.domainEngNm,
            domainExpln: d.domainExpln,
            domainType: domainType
          };
        });
        setDomainOptions(opts);
      })
      .catch((e) => setError('도메인 목록을 불러오지 못했습니다: ' + e.message))
      .finally(() => setDomainLoading(false));

    // 프로젝트 단어 목록 로드
    (async () => {
      try {
        const res = await getProjectWordList(pjtId);
        const list = Array.isArray(res?.data) ? res.data : [];
        setProjectWords(list);
      } catch (e) {
        setProjectWords([]);
      }
    })();
  }, [isOpen, pjtId]);

  // edit 모드일 때 열릴 때 초기값 주입
  useEffect(() => {
    if (isOpen && mode === 'edit' && initialValues) {
      form.setFieldsValue({
        trmdNm: initialValues.trmdNm ?? '',
        trmEngAbbrNm: initialValues.trmEngAbbrNm ?? '',
        trmExpln: initialValues.trmExpln ?? '',
        pjtDomainId: initialValues.pjtDomainId ?? undefined,
        synm: initialValues.synm ?? '',
      });
      setSelectedDomainInfo(initialValues.domainExpln ? { domainExpln: initialValues.domainExpln } : null);
      setMatchedWords([]);
    }
    if (isOpen && mode !== 'edit') {
      form.resetFields();
      setSelectedDomainInfo(null);
      setMatchedWords([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, mode, initialValues]);

  // 용어명 변경 시 단어 매칭 및 약어 자동 완성
  const handleValuesChange = (changed) => {
    if (Object.prototype.hasOwnProperty.call(changed, 'trmdNm')) {
      const termName = (changed.trmdNm ?? '').trim();
      const tokens = termName
        .split(/\s+/)
        .map((t) => t.trim())
        .filter((t) => t.length > 0);

      if (!tokens.length || !projectWords.length) {
        setMatchedWords([]);
        return;
      }

      // 토큰 순서대로 매칭(정확 일치 우선, 동의어 포함 일치 보조)
      const matched = [];
      tokens.forEach((tk) => {
        const exact = projectWords.find((w) => String(w.wordNm).trim() === tk);
        if (exact) {
          matched.push(exact);
          return;
        }
        const alt = projectWords.find((w) => {
          const syn = (w.synm || '').toString();
          if (!syn) return false;
          const parts = syn.split(/[\s,/|]+/).map((s) => s.trim()).filter(Boolean);
          return parts.includes(tk);
        });
        if (alt) matched.push(alt);
      });

      setMatchedWords(matched);

      // 매칭된 단어들로 영문약어 자동 완성(언더스코어 연결)
      if (matched.length) {
        const abbr = matched
          .map((w) => (w.wordEngAbbrNm || '').toString().trim())
          .filter(Boolean)
          .join('_');
        form.setFieldsValue({ trmEngAbbrNm: abbr });
      }
    }
  };

  const handleSubmit = async (values) => {
    if (!pjtId) {
      setError('프로젝트 정보가 없습니다.');
      return;
    }
    setError('');
    setLoading(true);
    try {
      const payload = { ...values };
      // 저장 시 용어명 공백 제거
      payload.trmdNm = (payload.trmdNm || '').replace(/\s+/g, '');
      if (payload.pjtDomainId === '' || payload.pjtDomainId == null) {
        delete payload.pjtDomainId;
      }
      if (mode === 'edit' && initialValues?.pjtTrmId) {
        await updateProjectTrm(pjtId, initialValues.pjtTrmId, payload);
      } else {
        await createProjectTrm(pjtId, payload);
      }
      form.resetFields();
      setSelectedDomainInfo(null);
      setMatchedWords([]);
      onSuccess && onSuccess();
      onClose();
    } catch (err) {
      setError(((mode === 'edit') ? '수정에 실패했습니다: ' : '등록에 실패했습니다: ') + (err?.response?.data?.message || err.message));
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    form.resetFields();
    setSelectedDomainInfo(null);
    setMatchedWords([]);
    setError('');
    onClose();
  };

  // 도메인 라벨(툴팁 아이콘 제거)
  const domainLabel = (
    <Space size={4}>
      <span>도메인</span>
    </Space>
  );

  const isEdit = mode === 'edit';

  return (
    <Modal
      title={
        <Space>
          {isEdit ? <EditOutlined /> : <PlusOutlined />}
          <span>{isEdit ? '프로젝트 용어 수정' : '프로젝트 용어 신규생성'}</span>
        </Space>
      }
      open={isOpen}
      onCancel={handleClose}
      width={900}
      footer={null}
    >
      <Form
        form={form}
        layout="vertical"
        onValuesChange={handleValuesChange}
        onFinish={handleSubmit}
        initialValues={{ trmdNm:'', trmExpln:'', trmEngAbbrNm:'', pjtDomainId:'', synm:'' }}
      >
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          <Form.Item label="용어명" name="trmdNm" rules={[{ required: true, message: '용어명을 입력하세요' }]}>
            <Input placeholder="예: 봉사 활동 내용" />
          </Form.Item>
          <Form.Item label="영문약어" name="trmEngAbbrNm">
            <Input placeholder="예: VOL_ACT_CONT" />
          </Form.Item>

          {/* 매칭된 단어 라벨 표시 */}
          <div style={{ gridColumn: '1 / span 2' }}>
            {matchedWords.length > 0 && (
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, flexWrap: 'wrap' }}>
                <span style={{ color: '#555', fontSize: 12, paddingTop: 4 }}>매칭된 단어:</span>
                <Space size={[6, 6]} wrap>
                  {matchedWords.map((w, idx) => (
                    <Tooltip
                      key={`${w.pjtWordId || w.wordNm}-${idx}`}
                      placement="top"
                      title={
                        <div>
                          <div><strong>영문명:</strong> {w.wordEngNm || '-'}</div>
                          <div><strong>설명:</strong> {w.wordExpln || '-'}</div>
                        </div>
                      }
                    >
                      <Tag color="blue">
                        {w.wordNm || '-'} | {w.wordEngAbbrNm || '-'} | {w.wordEngNm || '-'} | {w.wordExpln || '-'}
                      </Tag>
                    </Tooltip>
                  ))}
                </Space>
              </div>
            )}
          </div>

          {/* 도메인데이터ID 대신 도메인명 검색 Select */}
          <Form.Item label={domainLabel} name="pjtDomainId"
            extra={selectedDomainInfo ? (
              <div style={{ marginTop: 6, maxWidth: '100%' }}>
                <Tag color="geekblue" style={{ whiteSpace: 'normal', wordBreak: 'break-word', display: 'inline-block', maxWidth: '100%' }}>
                  {selectedDomainInfo.domainExpln ? ` ${selectedDomainInfo.domainExpln}` : '-'}
                </Tag>
              </div>
            ) : null}
          >
            <Select
              showSearch
              allowClear
              placeholder="도메인명, 영문명, 설명, 데이터유형으로 검색하세요"
              options={domainOptions}
              loading={domainLoading}
              optionFilterProp="label"
              filterOption={(input, option) => {
                const label = (option?.label ?? '').toLowerCase();
                const value = (option?.value ?? '').toString().toLowerCase();
                const domainNm = (option?.domainNm ?? '').toLowerCase();
                const domainEngNm = (option?.domainEngNm ?? '').toLowerCase();
                const domainExpln = (option?.domainExpln ?? '').toLowerCase();
                const domainType = (option?.domainType ?? '').toLowerCase();
                const search = input.toLowerCase();
                return (
                  label.includes(search) ||
                  value.includes(search) ||
                  domainNm.includes(search) ||
                  domainEngNm.includes(search) ||
                  domainExpln.includes(search) ||
                  domainType.includes(search)
                );
              }}
              onChange={(value, option) => {
                if (!value) {
                  setSelectedDomainInfo(null);
                  return;
                }
                setSelectedDomainInfo({
                  domainExpln: option?.domainExpln || '',
                });
              }}
            />
          </Form.Item>
          <Form.Item label="동의어" name="synm">
            <Input placeholder="예: 사용자번호" />
          </Form.Item>
          <Form.Item label="설명" name="trmExpln" rules={[{ required: true, message: '설명을 입력하세요' }]} style={{ gridColumn: '1 / span 2' }}>
            <Input.TextArea rows={3} placeholder="용어에 대한 설명을 입력하세요" />
          </Form.Item>
        </div>

        {error && <Alert message="오류" description={error} type="error" showIcon style={{ marginBottom: 16 }} />}

        <div style={{ textAlign: 'right' }}>
          <Button icon={<CloseOutlined />} onClick={handleClose} style={{ marginRight: 8 }}>
            취소
          </Button>
          <Button type="primary" htmlType="submit" icon={<SaveOutlined />} loading={loading}>
            {isEdit ? '수정' : '저장'}
          </Button>
        </div>
      </Form>
    </Modal>
  );
}

export default ProjectTrmCreateModal;
